import { UserService } from 'src/app/Services/user.service';import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { trigger, transition, style, animate } from '@angular/animations';

import { EmptyStateComponent } from 'src/app/Shared/Components/empty-state/empty-state.component';
import { tileRangeLabel } from 'src/app/helper/TileRangeLabel';
import { NotificationService } from 'src/app/Shared/prime-ng/notification.service';
import { DataResult, DataStateChangeEvent, GridDataResult, PageChangeEvent, State, pageSkip, pageTake, process } from 'src/app/Shared/prime-ng/grid-data';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';

@Component({
  selector: 'app-user-overview',
  standalone: true,
  imports: [PrimeNgModule, CommonModule,
    RouterModule,
    FormsModule,
    EmptyStateComponent
  ],
  templateUrl: './user-overview.component.html',
  styleUrl: './user-overview.component.css',
  animations: [
    trigger('cardAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class UserOverviewComponent implements OnInit, OnDestroy {
  public state: State = {
    skip: 0,
    take: 12,
    sort: [{ field: 'username', dir: 'asc' }]
  };

  public gridView: GridDataResult = { data: [], total: 0 };
  public allRows: any[] = [];
  public loading = false;
  public deleteDialog = false;
  public selectedUser: any | null = null;
  public searchText = '';
  public activeActionMenuKey: string | null = null;

  roleMapping: { dbName: string; displayName: string }[] = [
    { dbName: 'ADMIN', displayName: 'Admin' },
    { dbName: 'Inspector', displayName: 'Inspector' },
    { dbName: 'Auditor', displayName: 'Auditor' },
    { dbName: 'ExternalInspector', displayName: 'External Inspector' }
  ];

  private destroy$ = new Subject<void>();

  get footerRangeLabel(): string {
    return tileRangeLabel(
      this.gridView.total,
      this.state.take ?? 12,
      this.state.skip ?? 0,
    );
  }

  constructor(
    private userService: UserService,
    private notificationService: NotificationService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUsers(): void {
    this.loading = true;
    this.userService.getAllUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.allRows = rows ?? [];
          this.applyFilters();
          this.loading = false;
        },
        error: () => {
          this.allRows = [];
          this.applyFilters();
          this.loading = false;
          this.notification('Error loading users', 'error');
        }
      });
  }

  public onSearchChange(): void {
    this.state.skip = 0;
    this.applyFilters();
  }

  public clearSearch(): void {
    this.searchText = '';
    this.state.skip = 0;
    this.applyFilters();
  }

  private applyFilters(): void {
    let filtered = [...this.allRows];

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(item =>
        item.username?.toLowerCase().includes(search) ||
        item.email?.toLowerCase().includes(search) ||
        item.role?.toLowerCase().includes(search)
      );
    }

    this.gridView = process(filtered, this.state);
  }

  public pageChange(event: PageChangeEvent): void {
    this.state.skip = pageSkip(event);
    this.state.take = pageTake(event);
    this.applyFilters();
  }

  public toggleActionMenu(key: string | number, event: MouseEvent): void {
    event.stopPropagation();
    const menuKey = String(key);
    this.activeActionMenuKey = this.activeActionMenuKey === menuKey ? null : menuKey;
  }

  public isActionMenuOpen(key: string | number): boolean {
    return this.activeActionMenuKey === String(key);
  }

  public closeActionMenu(): void {
    this.activeActionMenuKey = null;
  }

  public openCreateDialog(): void {
    this.router.navigate(['/admin/user-detail']);
  }

  public openEditDialog(user: any): void {
    this.router.navigate(['/admin/user-detail', user.id]);
  }

  public deleteUser(user: any): void {
    this.selectedUser = user;
    this.deleteDialog = true;
  }

  public confirmDelete(): void {
    if (!this.selectedUser) return;

    const user = this.selectedUser;

    this.userService.deleteUser(user.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.allRows = this.allRows.filter(u => u.id !== user.id);
          this.notification(`User "${user.username}" deleted`, 'success');
          this.toggleDeleteDialog();
        },
        error: () => {
          this.notification('Error deleting user', 'error');
          this.toggleDeleteDialog();
        }
      });
  }

  public toggleDeleteDialog(): void {
    this.deleteDialog = !this.deleteDialog;
    if (!this.deleteDialog) {
      this.selectedUser = null;
    }
  }

  public getRoleDisplayName(dbName: string): string {
    const mapping = this.roleMapping.find(m => m.dbName === dbName);
    return mapping ? mapping.displayName : dbName;
  }
  public getLoginMethodDisplayName(user: any): string {    return user?.authMethods === 'Azure' || !!user?.objectId || !!user?.userPrincipalName ? 'Azure' : 'App';  }
  private notification(message: string, state: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: state, icon: true }
    });
  }
  public toggleUserStatus(user: any): void {
    const action = user.isActive ? 'deactivate' : 'activate';
    const serviceCall = user.isActive
      ? this.userService.deactivateUser(user.id)
      : this.userService.activateUser(user.id);

    serviceCall.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        user.isActive = !user.isActive;

        const statusText = user.isActive ? 'activated' : 'deactivated';
        this.notification(`User "${user.username}" ${statusText}`, 'success');
      },
      error: () => {
        this.notification(`Error ${action}ing user`, 'error');
      }
    });
  }
}
